import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Typography,
    Paper,
    TextField,
    IconButton,
    Chip,
    Radio,
    RadioGroup,
    Divider,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

const defaultTestPoints = [
    "North Wall",
    "South Wall",
    "East Wall",
    "West Wall",
    "Door",
    "Control Window",
    "Pen Panel",
    "Magnet Access",
];

export default function TestPointSelector({ testPoints, onSave, onChange }) {
    // Parse incoming test points if they're already polarized
    const parseTestPoints = (points) => {
        if (!points || points.length === 0)
            return { basePoints: [], polarization: "vertical" };

        // Check if points are already polarized
        const hasPolarization = points.some(
            (p) => p.includes(" - Vertical") || p.includes(" - Horizontal"),
        );

        if (!hasPolarization) {
            return { basePoints: points, polarization: "vertical" };
        }

        // Extract base names and detect polarization
        const baseNames = points.map((p) =>
            p.replace(/ - (Vertical|Horizontal)$/, ""),
        );
        const uniqueBaseNames = [...new Set(baseNames)];

        // Detect polarization setting
        const hasVertical = points.some((p) => p.includes(" - Vertical"));
        const hasHorizontal = points.some((p) => p.includes(" - Horizontal"));
        let detectedPolarization = "vertical";
        if (hasVertical && hasHorizontal) {
            detectedPolarization = "both";
        } else if (hasHorizontal) {
            detectedPolarization = "horizontal";
        }

        return {
            basePoints: uniqueBaseNames,
            polarization: detectedPolarization,
        };
    };

    const { basePoints, polarization: initialPolarization } = parseTestPoints(
        testPoints,
    );

    // Separate custom points from default points
    const initialCustomPoints = basePoints.filter(
        (point) => !defaultTestPoints.includes(point),
    );

    const [selectedPoints, setSelectedPoints] = useState(basePoints);
    const [customPoints, setCustomPoints] = useState(initialCustomPoints);
    const [newPointName, setNewPointName] = useState("");
    const [polarization, setPolarization] = useState(initialPolarization);

    // Generate test points with polarization
    const generateTestPointsWithPolarization = (points) => {
        const result = [];
        points.forEach((point) => {
            if (polarization === "both") {
                result.push(`${point} - Vertical`);
                result.push(`${point} - Horizontal`);
            } else if (polarization === "horizontal") {
                result.push(`${point} - Horizontal`);
            } else {
                result.push(`${point} - Vertical`);
            }
        });
        return result;
    };

    const handleToggle = (point) => {
        let newSelectedPoints;
        if (selectedPoints.includes(point)) {
            newSelectedPoints = selectedPoints.filter((p) => p !== point);
        } else {
            newSelectedPoints = [...selectedPoints, point];
        }
        setSelectedPoints(newSelectedPoints);
        if (onChange) {
            const pointsWithPolarization = generateTestPointsWithPolarization(
                newSelectedPoints,
            );
            onChange(pointsWithPolarization);
        }
    };

    const handlePolarizationChange = (event) => {
        const newPolarization = event.target.value;
        setPolarization(newPolarization);
        if (onChange && selectedPoints.length > 0) {
            // Recalculate test points with new polarization
            const pointsWithPolarization = generateTestPointsWithPolarization(
                selectedPoints,
            );
            onChange(pointsWithPolarization);
        }
    };

    const handleAddCustomPoint = () => {
        const trimmedName = newPointName.trim();
        if (
            trimmedName &&
            !customPoints.includes(trimmedName) &&
            !defaultTestPoints.includes(trimmedName)
        ) {
            setCustomPoints([...customPoints, trimmedName]);
            const newSelectedPoints = [...selectedPoints, trimmedName];
            setSelectedPoints(newSelectedPoints);
            setNewPointName("");
            if (onChange) {
                const pointsWithPolarization = generateTestPointsWithPolarization(
                    newSelectedPoints,
                );
                onChange(pointsWithPolarization);
            }
        }
    };

    const handleRemoveCustomPoint = (point) => {
        setCustomPoints(customPoints.filter((p) => p !== point));
        const newSelectedPoints = selectedPoints.filter((p) => p !== point);
        setSelectedPoints(newSelectedPoints);
        if (onChange) {
            const pointsWithPolarization = generateTestPointsWithPolarization(
                newSelectedPoints,
            );
            onChange(pointsWithPolarization);
        }
    };

    const handleSave = () => {
        const pointsWithPolarization = generateTestPointsWithPolarization(
            selectedPoints,
        );
        onSave(pointsWithPolarization);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddCustomPoint();
        }
    };

    const allTestPoints = [...defaultTestPoints, ...customPoints];

    return (
        <Box>
            <Paper elevation={0}>
                {/* Polarization Selection */}
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        border: 1,
                        borderColor: "divider",
                    }}
                >
                    <Typography
                        variant="body2"
                        fontWeight="medium"
                        gutterBottom
                    >
                        Polarization:
                    </Typography>
                    <RadioGroup
                        row
                        value={polarization}
                        onChange={handlePolarizationChange}
                    >
                        <FormControlLabel
                            value="vertical"
                            control={<Radio />}
                            label="Vertical Only"
                        />
                        <FormControlLabel
                            value="horizontal"
                            control={<Radio />}
                            label="Horizontal Only"
                        />
                        <FormControlLabel
                            value="both"
                            control={<Radio />}
                            label="Both (Doubles Test Points)"
                        />
                    </RadioGroup>
                    {polarization === "both" && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: "block" }}
                        >
                            Note: Selecting "Both" will create separate entries
                            for horizontal and vertical polarization for each
                            test point.
                        </Typography>
                    )}
                    {/* Preview of generated test points */}
                    {selectedPoints.length > 0 && (
                        <Typography
                            variant="body2"
                            fontWeight="medium"
                            gutterBottom
                            sx={{ mt: 2 }}
                        >
                            Test Points Preview (
                            {
                                generateTestPointsWithPolarization(
                                    selectedPoints,
                                ).length
                            }{" "}
                            total):
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body1" gutterBottom>
                    Select all test points that were measured:
                </Typography>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={
                                    allTestPoints.length > 0 &&
                                    allTestPoints.every((p) =>
                                        selectedPoints.includes(p),
                                    )
                                }
                                indeterminate={
                                    selectedPoints.length > 0 &&
                                    !allTestPoints.every((p) =>
                                        selectedPoints.includes(p),
                                    )
                                }
                                onChange={() => {
                                    const allSelected = allTestPoints.every(
                                        (p) => selectedPoints.includes(p),
                                    );
                                    const newSelectedPoints = allSelected
                                        ? []
                                        : [...allTestPoints];
                                    setSelectedPoints(newSelectedPoints);
                                    if (onChange) {
                                        const pointsWithPolarization = generateTestPointsWithPolarization(
                                            newSelectedPoints,
                                        );
                                        onChange(pointsWithPolarization);
                                    }
                                }}
                            />
                        }
                        label={
                            <Typography fontWeight="bold">
                                Select All
                            </Typography>
                        }
                    />
                    <Divider sx={{ my: 1 }} />
                    {defaultTestPoints.map((point) => (
                        <FormControlLabel
                            key={point}
                            control={
                                <Checkbox
                                    checked={selectedPoints.includes(point)}
                                    onChange={() => handleToggle(point)}
                                />
                            }
                            label={point}
                        />
                    ))}
                </FormGroup>

                {/* Custom Test Points */}
                {customPoints.length > 0 && (
                    <Box sx={{ mt: 3, mb: 2 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                        >
                            Custom Test Points:
                        </Typography>
                        <FormGroup>
                            {customPoints.map((point) => (
                                <Box
                                    key={point}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedPoints.includes(
                                                    point,
                                                )}
                                                onChange={() =>
                                                    handleToggle(point)
                                                }
                                            />
                                        }
                                        label={point}
                                        sx={{ flex: 1 }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                            handleRemoveCustomPoint(point)
                                        }
                                        aria-label="delete"
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </FormGroup>
                    </Box>
                )}

                {/* Add Custom Point */}
                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        label="Add Custom Test Point"
                        value={newPointName}
                        onChange={(e) => setNewPointName(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="e.g., Equipment Room"
                    />
                    <Button
                        variant="outlined"
                        onClick={handleAddCustomPoint}
                        disabled={!newPointName.trim()}
                        startIcon={<Add />}
                    >
                        Add
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
